import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";

import { formatDuration } from "@/utils/format-duration";

type CallDurationTimerProps = {
    /**
     * Start time of the call in ISO format
     */
    startTime: string | null;
    /**
     * Whether the call is currently active
     */
    isActive?: boolean;
    /**
     * Custom className for styling
     */
    className?: string;
    /**
     * Size variant for the timer display
     */
    size?: "sm" | "md" | "lg";
    /**
     * Whether to use monospace font (default: true for better readability)
     */
    monospace?: boolean;
};

export const CallDurationTimer = ({
    startTime,
    isActive = true,
    className = "",
    size = "md",
    monospace = true,
}: CallDurationTimerProps) => {
    const [currentDuration, setCurrentDuration] = useState<number>(0);

    const calculateDuration = useCallback(() => {
        if (!startTime) {
            setCurrentDuration(0);
            return;
        }

        const start = DateTime.fromISO(startTime);
        const now = DateTime.now();

        // Check if the start time is valid
        if (!start.isValid) {
            console.warn("Invalid start time:", startTime);
            setCurrentDuration(0);
            return;
        }

        const diffInSeconds = Math.floor(now.diff(start, "seconds").seconds);
        setCurrentDuration(Math.max(0, diffInSeconds)); // Ensure non-negative duration
    }, [startTime]);

    useEffect(() => {
        // Calculate initial duration
        calculateDuration();

        // Only update every second if call is active
        if (isActive) {
            const interval = setInterval(calculateDuration, 1000);
            return () => clearInterval(interval);
        }
    }, [calculateDuration, isActive]);

    // Don't render if call is not active
    if (!isActive) {
        return null;
    }

    // Size-based styling
    const sizeClasses = {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
    };

    const fontClasses = monospace ? "font-mono" : "font-medium";

    return (
        <span className={`${sizeClasses[size]} ${fontClasses} text-muted-foreground ${className}`}>
            {formatDuration(currentDuration)}
        </span>
    );
};
